// ============================================
// MyPage - Carbon Module (ES6)
// 복채 모달, 내역, 충전
// ============================================

import { showToast } from '../utils.js';
import { getDb } from './state.js';
import { getCurrentUser } from '../app/state.js';
import { getUserCarbon, setUserCarbon } from '../auth/state.js';
import { CURRENCY } from '../constants.js';
import { PAYMENT_ENDPOINTS } from '../shared/api-endpoints.js';
import { authFetch } from '../shared/auth-fetch.js';
import { isIAPInitialized, getServerProducts, purchaseProduct } from '../payment/iap.js';

// ============================================
// State
// ============================================

let carbonHistoryData = [];
let currentHistoryFilter = 'all';
let cachedProducts = [];

// ============================================
// Carbon Modal
// ============================================

export function openCarbonModal() {
  const modal = document.getElementById('carbonFullModal');
  if (modal) {
    modal.classList.add('open');
    updateCarbonDisplay();
    loadCarbonHistory();
    if (window.ModalManager) {
      window.ModalManager.push('carbonFullModal');
    }
  }
}

export function closeCarbonModal() {
  const modal = document.getElementById('carbonFullModal');
  if (modal) {
    modal.classList.remove('open');
  }
  if (window.ModalManager) {
    window.ModalManager.remove('carbonFullModal', true);
  }
}

export function updateCarbonDisplay() {
  const carbon = getUserCarbon() ?? 0;

  const myPageCarbon = document.getElementById('myPageCarbon');
  if (myPageCarbon) {
    myPageCarbon.textContent = carbon.toLocaleString();
  }

  const carbonFullAmount = document.getElementById('carbonFullAmount');
  if (carbonFullAmount) {
    carbonFullAmount.textContent = carbon.toLocaleString();
  }
}

// ============================================
// Carbon History
// ============================================

export async function loadCarbonHistory() {
  const user = getCurrentUser();
  if (!user) return;

  const list = document.getElementById('carbonHistoryList');
  if (!list) return;

  list.innerHTML = '<div style="display: flex; justify-content: center; padding: 40px;"><div class="spinner"></div></div>';

  try {
    const db = getDb();
    const snapshot = await db.collection('users').doc(user.uid)
      .collection('carbonHistory')
      .orderBy('createdAt', 'desc')
      .limit(50)
      .get();

    carbonHistoryData = [];
    snapshot.forEach(doc => {
      const data = doc.data();
      const description = data.description || data.reason || '';
      const type = data.type || (data.amount < 0 ? 'use' : 'charge');
      carbonHistoryData.push({
        id: doc.id,
        type: type,
        amount: Math.abs(data.amount || 0),
        description: description,
        createdAt: data.createdAt?.toDate?.() || new Date()
      });
    });

    renderCarbonHistory();
  } catch (e) {
    console.error('[MyPage/Carbon] 내역 로드 실패:', e);
    list.innerHTML = `
      <div class="carbon-history-empty">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--text-tertiary)" stroke-width="1.5">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
          <polyline points="14 2 14 8 20 8"/>
          <line x1="16" y1="13" x2="8" y2="13"/>
          <line x1="16" y1="17" x2="8" y2="17"/>
        </svg>
        <p>거래 내역을 불러올 수 없습니다</p>
      </div>
    `;
  }
}

export function filterCarbonHistory(filter) {
  currentHistoryFilter = filter;

  document.querySelectorAll('.carbon-filter-tab').forEach(tab => {
    tab.classList.toggle('active', tab.dataset.filter === filter);
  });

  renderCarbonHistory();
}

function renderCarbonHistory() {
  const list = document.getElementById('carbonHistoryList');
  if (!list) return;

  let filteredData = carbonHistoryData;
  if (currentHistoryFilter === 'charge') {
    filteredData = carbonHistoryData.filter(item => item.type === 'charge');
  } else if (currentHistoryFilter === 'use') {
    filteredData = carbonHistoryData.filter(item => item.type === 'use');
  }

  if (filteredData.length === 0) {
    list.innerHTML = `
      <div class="carbon-history-empty">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--text-tertiary)" stroke-width="1.5">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
          <polyline points="14 2 14 8 20 8"/>
          <line x1="16" y1="13" x2="8" y2="13"/>
          <line x1="16" y1="17" x2="8" y2="17"/>
        </svg>
        <p>거래 내역이 없습니다</p>
      </div>
    `;
    return;
  }

  let html = '';
  filteredData.forEach(item => {
    const isCharge = item.type === 'charge';
    const dateStr = formatDateTime(item.createdAt);
    const description = item.description || (isCharge ? `${CURRENCY.NAME} 충전` : `${CURRENCY.NAME} 사용`);

    html += `
      <div class="carbon-history-item">
        <div class="carbon-history-left">
          <span class="carbon-history-desc">${description}</span>
          <span class="carbon-history-date">${dateStr}</span>
        </div>
        <span class="carbon-history-amount">
          ${isCharge ? '+' : '-'}${Math.abs(item.amount).toLocaleString()} ${CURRENCY.UNIT}
        </span>
      </div>
    `;
  });

  list.innerHTML = html;
}

function formatDateTime(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}.${month}.${day} ${hours}:${minutes}`;
}

// ============================================
// Carbon Recharge Modal
// ============================================

export async function openCarbonRechargeModal() {
  const modal = document.getElementById('carbonRechargeModal');
  if (modal) {
    modal.classList.add('open');
    if (window.ModalManager) {
      window.ModalManager.push('carbonRechargeModal');
    }
    await loadCarbonProducts();
  }
}

export function closeCarbonRechargeModal() {
  const modal = document.getElementById('carbonRechargeModal');
  if (modal) modal.classList.remove('open');
  if (window.ModalManager) {
    window.ModalManager.remove('carbonRechargeModal', true);
  }
}

// ============================================
// Dynamic Product Loading
// ============================================

export async function loadCarbonProducts() {
  const container = document.getElementById('carbonPackageList');
  if (!container) return;

  // Show loading
  container.innerHTML = '<div style="display: flex; justify-content: center; padding: 40px;"><div class="spinner"></div></div>';

  try {
    // 앱 환경에서 IAP 모듈이 초기화되어 있으면 그 상품 목록 사용
    const isApp = window.Capacitor?.isNativePlatform?.();
    if (isApp && isIAPInitialized()) {
      const iapProducts = getServerProducts();
      if (iapProducts && iapProducts.length > 0) {
        cachedProducts = iapProducts;
        renderCarbonPackages(iapProducts);
        console.log('[Carbon] 앱 환경 - IAP 상품 목록 사용');
        return;
      }
    }

    // 웹 환경 또는 IAP 미초기화 시 서버에서 직접 로드
    const response = await authFetch(PAYMENT_ENDPOINTS.getProducts, {
      method: 'POST',
      body: {}
    });

    if (!response.ok) {
      throw new Error('상품 로드 실패');
    }

    const result = await response.json();
    if (result.success && result.products) {
      cachedProducts = result.products;
      renderCarbonPackages(result.products);
    } else {
      throw new Error('상품 데이터 없음');
    }
  } catch (e) {
    console.error('[Carbon] 상품 로드 실패:', e);
    container.innerHTML = `
      <div style="text-align: center; padding: 40px; color: var(--text-secondary);">
        <p>상품 목록을 불러올 수 없습니다</p>
        <button onclick="window.CarbonModule?.loadCarbonProducts()" style="margin-top: 12px; padding: 8px 16px; border-radius: 8px; background: var(--primary); color: white; border: none; cursor: pointer;">
          다시 시도
        </button>
      </div>
    `;
    // Update notice on error
    const notice = document.getElementById('carbonRechargeNotice');
    if (notice) {
      notice.textContent = '상품 로드에 실패했습니다';
    }
  }
}

function renderCarbonPackages(products) {
  const container = document.getElementById('carbonPackageList');
  if (!container) return;

  // Update notice
  const notice = document.getElementById('carbonRechargeNotice');
  if (notice) {
    notice.textContent = '충전 후 즉시 사용 가능합니다';
  }

  // Sort by order
  const sorted = [...products].sort((a, b) => (a.order || 0) - (b.order || 0));

  let html = '';
  sorted.forEach(product => {
    const hasBonus = product.bonus && product.bonus > 0;
    const badgeHtml = product.badge ? `<span class="package-badge">${product.badge}</span>` : '';
    const bonusHtml = hasBonus ? `<span class="package-bonus">+${product.bonus.toLocaleString()} 보너스</span>` : '';

    html += `
      <div class="carbon-package-item-v2" data-product-id="${product.id}" data-amount="${product.amount}" data-price="${product.price}">
        ${badgeHtml}
        <div class="package-amount">
          <span class="amount-value">${product.amount.toLocaleString()}</span>
          <span class="amount-unit">${CURRENCY.UNIT}</span>
        </div>
        ${bonusHtml}
        <div class="package-price">₩${product.price.toLocaleString()}</div>
      </div>
    `;
  });

  container.innerHTML = html;

  // Add click handlers
  container.querySelectorAll('.carbon-package-item-v2').forEach(item => {
    item.addEventListener('click', () => {
      const productId = item.dataset.productId;
      const amount = parseInt(item.dataset.amount, 10);
      const price = parseInt(item.dataset.price, 10);
      selectCarbonPackage(productId, amount, price);
    });
  });
}

// ============================================
// Purchase Flow
// ============================================

export async function selectCarbonPackage(productId, amount, price) {
  const product = cachedProducts.find(p => p.id === productId);
  if (!product) {
    showToast('상품 정보를 찾을 수 없습니다');
    return;
  }

  // Check platform
  const isAndroid = window.Capacitor?.getPlatform?.() === 'android';
  const isIOS = window.Capacitor?.getPlatform?.() === 'ios';

  if (isAndroid && product.googleProductId) {
    await purchaseGooglePlay(product);
  } else if (isIOS && product.appleProductId) {
    await purchaseAppleStore(product);
  } else {
    // Web or no product ID configured
    showToast(`${amount.toLocaleString()} ${CURRENCY.UNIT} (₩${price.toLocaleString()}) - 앱에서 결제해 주세요`);
  }
}

async function purchaseGooglePlay(product) {
  // IAP 모듈의 purchaseProduct 사용 (product.id = Firestore 상품 ID)
  await purchaseProduct(product.id);
}

async function purchaseAppleStore(product) {
  // IAP 모듈의 purchaseProduct 사용 (product.id = Firestore 상품 ID)
  await purchaseProduct(product.id);
}

// ============================================
// Server Verification (called by IAP plugin callback)
// ============================================

export async function verifyGooglePurchase(productId, purchaseToken, orderId) {
  try {
    const response = await authFetch(PAYMENT_ENDPOINTS.verifyGoogle, {
      method: 'POST',
      body: { productId, purchaseToken, orderId }
    });

    const result = await response.json();

    if (result.success) {
      showToast(result.message || '충전 완료!');
      // Update local carbon balance
      const user = getCurrentUser();
      if (user) {
        const newBalance = getUserCarbon() + result.carbonGranted;
        setUserCarbon(newBalance);
        updateCarbonDisplay();
      }
      closeCarbonRechargeModal();
      loadCarbonHistory();
      return true;
    } else {
      showToast(result.error?.message || '결제 검증에 실패했습니다');
      return false;
    }
  } catch (e) {
    console.error('[Carbon] Google 결제 검증 실패:', e);
    showToast('결제 검증에 실패했습니다');
    return false;
  }
}

export async function verifyApplePurchase(productId, receiptData, transactionId) {
  try {
    const response = await authFetch(PAYMENT_ENDPOINTS.verifyApple, {
      method: 'POST',
      body: { productId, receiptData, transactionId }
    });

    const result = await response.json();

    if (result.success) {
      showToast(result.message || '충전 완료!');
      const user = getCurrentUser();
      if (user) {
        const newBalance = getUserCarbon() + result.carbonGranted;
        setUserCarbon(newBalance);
        updateCarbonDisplay();
      }
      closeCarbonRechargeModal();
      loadCarbonHistory();
      return true;
    } else {
      showToast(result.error?.message || '결제 검증에 실패했습니다');
      return false;
    }
  } catch (e) {
    console.error('[Carbon] Apple 결제 검증 실패:', e);
    showToast('결제 검증에 실패했습니다');
    return false;
  }
}

export function purchaseCarbon(amount) {
  showToast('상품을 선택해 주세요');
}

// Legacy alias
export function updateCarbonBalance() {
  updateCarbonDisplay();
}

// ============================================
// IAP 결제 완료 이벤트 리스너
// ============================================

// IAP 모듈에서 결제 완료 시 발생하는 이벤트 처리
window.addEventListener('purchaseCompleted', (event) => {
  const { carbonAmount } = event.detail;
  console.log('[Carbon] Purchase completed, carbon granted:', carbonAmount);

  // 카본 디스플레이 업데이트
  updateCarbonDisplay();

  // 충전 모달 닫기
  closeCarbonRechargeModal();

  // 카본 내역 새로고침
  loadCarbonHistory();
});
